import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft, Webhook, Plus, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';

export default function WebhooksSettings() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [webhooks, setWebhooks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    url: '',
    events: [],
  });

  const availableEvents = [
    'member.invited',
    'member.removed',
    'team.created',
    'team.deleted',
    'billing.invoice.created',
    'byodb.connected',
  ];

  useEffect(() => {
    fetchWebhooks();
  }, [orgId]);

  const fetchWebhooks = async () => {
    try {
      const response = await apiClient.get(`/webhooks/orgs/${orgId}`);
      setWebhooks(response.data);
    } catch (error) {
      console.error('Failed to fetch webhooks:', error);
    }
  };

  const handleCreate = async () => {
    try {
      await apiClient.post(`/webhooks/orgs/${orgId}`, newWebhook);
      toast.success('Webhook created');
      setNewWebhook({ url: '', events: [] });
      setDialogOpen(false);
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to create webhook');
    }
  };

  const handleToggle = async (webhookId, currentlyEnabled) => {
    try {
      await apiClient.put(`/webhooks/orgs/${orgId}/${webhookId}/toggle?enabled=${!currentlyEnabled}`);
      toast.success(currentlyEnabled ? 'Webhook disabled' : 'Webhook enabled');
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to toggle webhook');
    }
  };

  const handleDelete = async (webhookId) => {
    if (!confirm('Are you sure you want to delete this webhook?')) return;

    try {
      await apiClient.delete(`/webhooks/orgs/${orgId}/${webhookId}`);
      toast.success('Webhook deleted');
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const toggleEvent = (event) => {
    if (newWebhook.events.includes(event)) {
      setNewWebhook({
        ...newWebhook,
        events: newWebhook.events.filter((e) => e !== event),
      });
    } else {
      setNewWebhook({
        ...newWebhook,
        events: [...newWebhook.events, event],
      });
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/enterprise/orgs/${orgId}/settings`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">Webhooks</h1>
            <p className="text-gray-600 mt-1">Automate workflows with webhook events</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Create Webhook
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Webhook</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Webhook URL *</Label>
                <Input
                  value={newWebhook.url}
                  onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                  placeholder="https://your-server.com/webhook"
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div>
                <Label>Events *</Label>
                <div className="space-y-2 mt-2">
                  {availableEvents.map((event) => (
                    <div key={event} className="flex items-center gap-2">
                      <Checkbox
                        id={event}
                        checked={newWebhook.events.includes(event)}
                        onCheckedChange={() => toggleEvent(event)}
                      />
                      <Label htmlFor={event} className="cursor-pointer font-mono text-sm">
                        {event}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              <Button
                onClick={handleCreate}
                disabled={!newWebhook.url || newWebhook.events.length === 0}
                className="w-full"
              >
                Create Webhook
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {webhooks.length > 0 ? (
        <div className="space-y-4">
          {webhooks.map((webhook) => (
            <Card key={webhook.id} className="p-6 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-mono text-sm font-semibold text-gray-900 break-all">
                      {webhook.url}
                    </h3>
                    {webhook.enabled ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-success/10 text-success">
                        Active
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                        Disabled
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {webhook.events?.map((event) => (
                      <span key={event} className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 font-mono">
                        {event}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggle(webhook.id, webhook.enabled)}
                  >
                    {webhook.enabled ? (
                      <ToggleRight className="w-5 h-5 text-success" />
                    ) : (
                      <ToggleLeft className="w-5 h-5 text-gray-400" />
                    )}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(webhook.id)}>
                    <Trash2 className="w-4 h-4 text-gray-400 hover:text-error" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 rounded-lg text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Webhook className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
            No webhooks configured
          </h3>
          <p className="text-gray-600 mb-6">
            Create webhooks to receive real-time events from HumaTwin
          </p>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create First Webhook
          </Button>
        </Card>
      )}
    </div>
  );
}
